import {
  NonPromotionalMessage,
  PromotionalMessage,
} from "../../types/prodcatBaseTypes";
import { Message } from "../../types/types";
import { cleanHtml } from "../string/cleanHtml";

type GetPromoMessagingResponse = {
  promoDrawerMessages: PromotionalMessage[];
  pageMessages: PromotionalMessage[];
  featureDrawerMessages: NonPromotionalMessage[];
  additionalMessages: NonPromotionalMessage[];
};

function cleanMessage(message: Message) {
  return {
    ...message,
    description: cleanHtml(message.description) || null,
  };
}

function isIFCMessage(message: Message) {
  return /interest free credit/gim.test(message.title);
}

function concatIfUnique(arr: Array<Message>, item: Message) {
  return arr.find(
    ({ title, description }) =>
      title === item.title && description === item.description,
  )
    ? arr
    : arr.concat(item);
}

function addTo(
  acc: GetPromoMessagingResponse,
  message: Message,
  section:
    | "pageMessages"
    | "promoDrawerMessages"
    | "featureDrawerMessages"
    | "additionalMessages",
) {
  return {
    ...acc,
    [section]: concatIfUnique(acc[section], message),
  };
}

export const isPromotionalMessage = (
  message: NonPromotionalMessage | PromotionalMessage,
): message is PromotionalMessage => {
  return "promotionGroup" in message && message.promotionGroup !== undefined;
};

export const pricingGroups = [
  "Save",
  "Reduced To Clear",
  "Special Buy",
  "Price Match",
  "Introductory Offer",
];

export const getPromoMessaging = (
  messages: Message[],
  isCobeCreditOffers = false,
): GetPromoMessagingResponse =>
  messages.map(cleanMessage).reduce(
    (
      acc: GetPromoMessagingResponse,
      message: NonPromotionalMessage | PromotionalMessage,
    ) => {
      const { title, type } = message;
      if (isCobeCreditOffers && isIFCMessage(message)) {
        return acc;
      }

      if (isPromotionalMessage(message)) {
        if (pricingGroups.includes(message.promotionGroup)) {
          return addTo(acc, message, "pageMessages");
        } else {
          return addTo(acc, message, "promoDrawerMessages");
        }
      }

      switch (type) {
        case "feature": {
          if (title === "60 Night Comfort Trial") {
            return addTo(acc, message, "pageMessages");
          }
          return addTo(acc, message, "featureDrawerMessages");
        }
        case "endorsement": {
          return addTo(acc, message, "featureDrawerMessages");
        }
        case "additional": {
          return addTo(acc, message, "additionalMessages");
        }
        default: {
          return addTo(acc, message, "promoDrawerMessages");
        }
      }
    },
    {
      promoDrawerMessages: [],
      pageMessages: [],
      featureDrawerMessages: [],
      additionalMessages: [],
    },
  );

export type MessageWithClippedTitle = PromotionalMessage & {
  clippedTitle: string;
};

export type MessageWithCategoryAndType = MessageWithClippedTitle & {
  category: string;
  tag: string | null;
};

export const trimMessageTerms = (
  message: PromotionalMessage,
): MessageWithClippedTitle => {
  const termsToRemove = [
    " when bought with this item",
    " when you purchase it with this item",
    " when you buy it with this item",
    " when you purchase this item between",
    " (via promo code",
    " (via redemption",
  ];

  const messageWithClippedTitle = Object.assign(
    { clippedTitle: message.title },
    message,
  );
  termsToRemove.forEach((termToRemove) => {
    const indexOfTerm = messageWithClippedTitle.clippedTitle
      .toLowerCase()
      .indexOf(termToRemove);
    if (indexOfTerm > 0) {
      messageWithClippedTitle.clippedTitle =
        messageWithClippedTitle.clippedTitle.substring(0, indexOfTerm);
    }
  });
  return messageWithClippedTitle;
};

export const getMessageRankByTitle = (messageTitle: string) => {
  if (/\bfree\b/gi.test(messageTitle) && /\bworth\b/gi.test(messageTitle)) {
    return 10;
  }
  if (/\bcashback\b/gi.test(messageTitle)) {
    return 9;
  }
  if (/\bfree\b/gi.test(messageTitle)) {
    return 8;
  }
  if (
    /\breceive\b/gi.test(messageTitle) &&
    /no extra cost/gi.test(messageTitle)
  ) {
    return 7;
  }
  if (/\bguarantee\b/gi.test(messageTitle)) {
    return 6;
  }
  if (
    /\bsave\b/gi.test(messageTitle) ||
    /£.+\soff/gi.test(messageTitle) ||
    /.+%\soff/gi.test(messageTitle)
  ) {
    return 5;
  }
  if (/£/gi.test(messageTitle)) {
    return 4;
  }
  if (/\bclaim\b/gi.test(messageTitle)) {
    return 3;
  }
  if (/enter the draw/gi.test(messageTitle)) {
    return 2;
  }
  return 1;
};

type CategoryAndTagLookupType = {
  [key: string]: {
    category: string;
    tag: string | null;
  };
};

export const giftWithPurchaseTypes = {
  "Combination offer - 1 item free on qualifying item": {
    category: "offers",
    tag: "Free gift",
  },
  "Combination offer - £ off qualifying item": {
    category: "offers",
    tag: "Bundle offer",
  },
  "Combination offer - % off qualifying item": {
    category: "offers",
    tag: "Bundle offer",
  },
  "Combination offer - Free product C when buy product A & B": {
    category: "offers",
    tag: "Buy 2, get 1 free",
  },
  "Spend Stretch- GWP ": {
    category: "offers",
    tag: "Multibuy offer",
  },
  "Spend Stretch- GWP": {
    category: "offers",
    tag: "Multibuy offer",
  },
};

export const combinationMyJLOfferTypes = [
  "My JL Save (%)",
  "My JL Save (£)",
  "MyJL Additional Save (£)",
  "MyJL Additional Save (%)",
];

export const combinationOfferTypesExclMyJL = [
  "Combination offer - £ off discounted item",
  "Combination offer - £ off qualifying item",
  "Combination offer - % off discounted item",
  "Combination offer - % off qualifying item",
  "Combination offer - 1 item free on qualifying item",
  "Combination offer - 1 item free (message on free item)",
  "Combination offer - Get one item for a fixed price",
  "Combination offer - Free product C when buy product A & B",
  "Spend Stretch- £ off ",
  "Spend Stretch- GWP ",
  "Spend Stretch- £ off",
  "Spend Stretch- GWP",
  "Tiered Spend Stretch",
];
export const combinationOfferTypes = [
  ...combinationOfferTypesExclMyJL,
  ...combinationMyJLOfferTypes,
];

const categoryAndTagLookup: CategoryAndTagLookupType = {
  "Claim - cashback": {
    category: "claims",
    tag: "Cashback offer",
  },
  "Claim - free product/service": {
    category: "claims",
    tag: "Claim item/service",
  },
  "Guarantee - Claim": {
    category: "claims",
    tag: "Extra guarantee",
  },
  "Guarantee - JL": {
    category: "offers",
    tag: "Extra guarantee",
  },
  "Half Price Install (Coupon)": {
    category: "offers",
    tag: "50% off install",
  },
  "Half Price Install (Coupon App only)": {
    category: "offers",
    tag: "50% off install",
  },
  "Half Price Install (PIM Service)": {
    category: "services",
    tag: "50% off install",
  },
  "Free Install & Recycling": {
    category: "services",
    tag: "Free service",
  },
  "Free Recycling": {
    category: "services",
    tag: "Free recycling",
  },
  "Free Installation": {
    category: "services",
    tag: "Free installation",
  },
  "Free Install/Recycling - D2C": {
    category: "services",
    tag: "Free service",
  },
  "Free Mattress Recycling": {
    category: "services",
    tag: "Free recycling",
  },
  "Free TV Recycling": {
    category: "services",
    tag: "Free recycling",
  },
  "Free TV Wall Mount Install": {
    category: "services",
    tag: "Free installation",
  },
  "Trade In - Coupon": {
    category: "offers",
    tag: "Trade in offer",
  },
  "Trade In - Coupon (App only)": {
    category: "offers",
    tag: "Trade in offer",
  },
  "Trade In - Claim": {
    category: "claims",
    tag: "Trade in offer",
  },
  "Trade In - Enhanced": {
    category: "offers",
    tag: "Trade in offer",
  },
  "Combination offer - £ off discounted item": {
    category: "offers",
    tag: "Bundle offer",
  },
  "Combination offer - % off discounted item": {
    category: "offers",
    tag: "Bundle offer",
  },
  "Combination offer - 1 item free (message on free item)": {
    category: "offers",
    tag: "Free gift",
  },
  "Combination offer - Get one item for a fixed price": {
    category: "offers",
    tag: "Combination offer",
  },
  "My JL Save (£)": {
    category: "offers",
    tag: "Member savings",
  },
  "My JL Save (%)": {
    category: "offers",
    tag: "Member savings",
  },
  "MyJL Additional Save (£)": {
    category: "offers",
    tag: "Member savings",
  },
  "MyJL Additional Save (%)": {
    category: "offers",
    tag: "Member savings",
  },
  "Spend Stretch- £ off ": {
    category: "offers",
    tag: "Multibuy offer",
  },
  "Spend Stretch- £ off": {
    category: "offers",
    tag: "Multibuy offer",
  },
  "Tiered Spend Stretch": {
    category: "offers",
    tag: "Multibuy offer",
  },
  "Free next day delivery (Coupon)": {
    category: "offers",
    tag: "Free delivery",
  },
  "Combination offer - £ off discounted item (App Only)": {
    category: "offers",
    tag: "Bundle offer",
  },
  "Combination offer - £ off qualifying item (App Only)": {
    category: "offers",
    tag: "Bundle offer",
  },
  "Combination offer - % off discounted item (App Only)": {
    category: "offers",
    tag: "Bundle offer",
  },
  "Combination offer - % off qualifying item (App Only)": {
    category: "offers",
    tag: "Bundle offer",
  },
  "Combination offer - 1 item free on qualifying item (App Only)": {
    category: "offers",
    tag: "Free gift",
  },
  "Combination offer - 1 item free (message on free item) (App Only)": {
    category: "offers",
    tag: "Free gift",
  },
  "Combination offer - Free product C when buy product A & B (App Only)": {
    category: "offers",
    tag: "Buy 2, get 1 free",
  },
  "Combination offer - Get one item for a fixed price (App Only)": {
    category: "offers",
    tag: "Bundle offer",
  },
  "My JL Save (£) (App Only)": {
    category: "offers",
    tag: "Member savings",
  },
  "My JL Save (%) (App Only)": {
    category: "offers",
    tag: "Member savings",
  },
  "MyJL Additional Save (£) (App Only)": {
    category: "offers",
    tag: "Member savings",
  },
  "MyJL Additional Save (%) (App Only)": {
    category: "offers",
    tag: "Member savings",
  },
  "Spend Stretch- GWP (App Only)": {
    category: "offers",
    tag: "Multibuy offer",
  },
  "Spend Stretch- £ off (App Only)": {
    category: "offers",
    tag: "Multibuy offer",
  },
  "Tiered Spend Stretch (App Only)": {
    category: "offers",
    tag: "Multibuy offer",
  },
  "Free next day delivery (Coupon) (App Only)": {
    category: "offers",
    tag: "Free delivery",
  },
  "Free Gift in bundle": {
    category: "offers",
    tag: "Gift included",
  },
  "Free Item in bundle": {
    category: "offers",
    tag: "Gift included",
  },
  "Ad hoc promotion": {
    category: "offers",
    tag: "Offer available",
  },
  "Save (£)": {
    category: "offers",
    tag: "Save with code",
  },
  "Save (%)": {
    category: "offers",
    tag: "Save with code",
  },
  "Additional Save (£)": {
    category: "offers",
    tag: "Save more with code",
  },
  "Additional Save (%)": {
    category: "offers",
    tag: "Save more with code",
  },
  "Trade In - Enhanced (non - coupon)": {
    category: "offers",
    tag: "Trade in offer",
  },
  ...giftWithPurchaseTypes,
};

type CategoriseMessagesResponse = MessageWithCategoryAndType[];

export const categoriseMessages = (
  messages: MessageWithClippedTitle[],
): CategoriseMessagesResponse => {
  return messages.map((message) => {
    if (isPromotionalMessage(message)) {
      const categorisation =
        categoryAndTagLookup[
          message.promotionType as keyof CategoryAndTagLookupType
        ];

      if (!categorisation) {
        return Object.assign(
          { category: "offers", tag: "Offer available" },
          message,
        );
      } else {
        const { category, tag } = categorisation;
        return Object.assign({ category, tag }, message);
      }
    } else {
      return Object.assign(
        { category: "offers", tag: "Offer available" },
        message,
      );
    }
  });
};

export const groupMessagesByCategory = (
  messagesWithCategoryAndType: MessageWithCategoryAndType[],
) => {
  return messagesWithCategoryAndType.reduce(
    (
      acc: {
        offers: MessageWithCategoryAndType[];
        services: MessageWithCategoryAndType[];
        claims: MessageWithCategoryAndType[];
      },
      message,
    ) => {
      if (message.category === "offers") {
        acc.offers.push(message);
      } else if (message.category === "services") {
        acc.services.push(message);
      } else if (message.category === "claims") {
        acc.claims.push(message);
      }
      return acc;
    },
    { offers: [], services: [], claims: [] },
  );
};
